import React from 'react';
import { Loader2, Copy, Check, Handshake, X } from 'lucide-react';
import type { MyCommissionReport } from '../../types/selfService';
import { useSingleStationContext } from '../../components/self-service/admin/useAdminContext';
import AdminPageHeader from '../../components/self-service/admin/AdminPageHeader';
import { apiClient } from '../../lib/apiClient';

const formatDollars = (cents: number) =>
  (cents / 100).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const formatMonth = (month: string) => {
  const [y, m] = month.split('-').map(Number);
  return new Date(Date.UTC(y, (m ?? 1) - 1, 1)).toLocaleDateString('en-US', {
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });
};

// CTV-673: a rep's own referral stats. The API resolves the caller's linked
// salesperson record and returns only that slice; every role can load this
// page, but it only ever shows the caller's own numbers.
const PublisherMyReferralsPage: React.FC = () => {
  const { slug } = useSingleStationContext();
  const [report, setReport] = React.useState<MyCommissionReport | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [copied, setCopied] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const data = await apiClient.post<MyCommissionReport>(
          '/salespeople/my-commission-report',
          { slug },
        );
        if (!cancelled) setReport(data);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  const shareUrl = (code: string) => {
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    return `${origin}/${slug}?ref=${code}`;
  };

  const handleCopy = async (code: string) => {
    try {
      await navigator.clipboard.writeText(shareUrl(code));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // ignore — the URL is visible in the tooltip either way
    }
  };

  const rep = report?.rep ?? null;
  // CTV-808: a station that has not configured commissions shows a rep no
  // commission copy at all. "Configured" is the rep's EFFECTIVE rate (their
  // override, else the station default) being set, so a per-rep override
  // with no station default still counts. Until the report loads there is
  // no rate to show, and the neutral copy is right for that moment too.
  const commissionsEnabled = rep?.effective_commission_percent != null;

  return (
    <div className="max-w-3xl">
      <AdminPageHeader
        title="My Referrals"
        scopeMode="bound"
        currentSlug={slug}
        subtitle={
          commissionsEnabled
            ? 'Campaigns credited to your referral code, and the commission they have earned.'
            : 'Campaigns credited to your referral code.'
        }
      />

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700 flex items-start justify-between gap-3">
          <span>{error}</span>
          <button onClick={() => setError(null)} aria-label="Dismiss"><X className="w-4 h-4" /></button>
        </div>
      )}

      {loading ? (
        <div className="text-sm text-slate-500">
          <Loader2 className="w-4 h-4 animate-spin inline mr-2" />
          Loading…
        </div>
      ) : !rep ? (
        <div className="bg-white border border-slate-200 rounded-xl p-10 text-center">
          <Handshake className="w-8 h-8 text-slate-400 mx-auto mb-2" />
          <p className="text-slate-700 font-medium">You&apos;re not set up as a salesperson yet</p>
          <p className="text-sm text-slate-500 mt-1">
            Ask your station manager to add you on the Referrals page using this login&apos;s
            email address. Once linked, your referral code and stats appear here.
          </p>
        </div>
      ) : (
        <>
          <div className="bg-white border border-slate-200 rounded-xl p-4 mb-4 flex items-center justify-between gap-4 flex-wrap">
            <div>
              <div className="text-xs uppercase tracking-wide text-slate-500 mb-0.5">Your referral code</div>
              <div className="flex items-center gap-3">
                <code className="px-2 py-1 rounded bg-slate-100 text-slate-900 font-mono text-lg tracking-widest">
                  {rep.code}
                </code>
                <button
                  type="button"
                  onClick={() => handleCopy(rep.code)}
                  title={shareUrl(rep.code)}
                  className="inline-flex items-center gap-1.5 whitespace-nowrap px-3 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 hover:bg-slate-50 rounded-md"
                >
                  {copied ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
                  {copied ? 'Copied' : 'Copy Share Link'}
                </button>
                {!rep.active && (
                  <span className="text-[11px] font-medium px-2 py-0.5 rounded bg-slate-200 text-slate-700">
                    Disabled
                  </span>
                )}
              </div>
            </div>
            {commissionsEnabled && (
              <div className="text-right">
                <div className="text-xs uppercase tracking-wide text-slate-500 mb-0.5">Commission rate</div>
                <div className="text-lg font-semibold text-slate-900 tabular-nums">
                  {rep.effective_commission_percent}%
                </div>
              </div>
            )}
          </div>

          <div className={`grid ${commissionsEnabled ? 'grid-cols-3' : 'grid-cols-2'} gap-3 mb-4`}>
            <div className="bg-white border border-slate-200 rounded-xl p-4">
              <div className="text-xs uppercase tracking-wide text-slate-500 mb-1" title="Campaigns that have collected at least one payment. Attributed campaigns awaiting payment appear in the list below.">Paid Campaigns</div>
              <div className="text-2xl font-bold text-slate-900 tabular-nums">{rep.campaign_count}</div>
            </div>
            <div className="bg-white border border-slate-200 rounded-xl p-4">
              <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">Net revenue</div>
              <div className="text-2xl font-bold text-slate-900 tabular-nums">
                {formatDollars(rep.total_net_revenue_cents)}
              </div>
            </div>
            {commissionsEnabled && (
              <div className="bg-white border border-slate-200 rounded-xl p-4">
                <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">Commission earned</div>
                <div className="text-2xl font-bold text-emerald-700 tabular-nums">
                  {formatDollars(rep.total_commission_cents)}
                </div>
              </div>
            )}
          </div>

          {rep.monthly.length > 0 && (
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden mb-4">
              <div className="px-5 py-3 text-xs uppercase tracking-wide text-slate-500 border-b border-slate-200">
                By month
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[10px] font-medium uppercase tracking-[0.12em] text-slate-500 bg-slate-50/60 border-b border-slate-200">
                      <th className="px-5 py-2.5 font-medium">Month</th>
                      <th className="px-3 py-2.5 font-medium text-right">Campaigns</th>
                      <th className={`${commissionsEnabled ? 'px-3' : 'px-5'} py-2.5 font-medium text-right`}>Net revenue</th>
                      {commissionsEnabled && (
                        <th className="px-5 py-2.5 font-medium text-right">Commission</th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {rep.monthly.map((m) => (
                      <tr key={m.month}>
                        <td className="px-5 py-2.5 text-slate-900">{formatMonth(m.month)}</td>
                        <td className="px-3 py-2.5 text-right tabular-nums text-slate-700">{m.campaign_count}</td>
                        <td className={`${commissionsEnabled ? 'px-3' : 'px-5'} py-2.5 text-right tabular-nums text-slate-700`}>
                          {formatDollars(m.net_revenue_cents)}
                        </td>
                        {commissionsEnabled && (
                          <td className="px-5 py-2.5 text-right tabular-nums text-emerald-700">
                            {formatDollars(m.commission_cents)}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {rep.campaigns.length > 0 && (
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
              <div className="px-5 py-3 text-xs uppercase tracking-wide text-slate-500 border-b border-slate-200">
                Campaigns
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[10px] font-medium uppercase tracking-[0.12em] text-slate-500 bg-slate-50/60 border-b border-slate-200">
                      <th className="px-5 py-2.5 font-medium">Advertiser</th>
                      {commissionsEnabled && (
                        <th className="px-3 py-2.5 font-medium text-right">Rate</th>
                      )}
                      <th className={`${commissionsEnabled ? 'px-3' : 'px-5'} py-2.5 font-medium text-right`}>Net revenue</th>
                      {commissionsEnabled && (
                        <th className="px-5 py-2.5 font-medium text-right">Commission</th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {rep.campaigns.map((c) => (
                      <tr key={c.submission_id}>
                        <td className="px-5 py-2.5 text-slate-900">
                          {c.advertiser_name ?? <span className="text-slate-400">Advertiser</span>}
                        </td>
                        {commissionsEnabled && (
                          <td className="px-3 py-2.5 text-right tabular-nums text-slate-700">
                            {c.commission_percent != null ? (
                              `${c.commission_percent}%`
                            ) : (
                              // CTV-673: pure lock-at-submission, no snapshot
                              // means this campaign earns no commission, ever.
                              <span
                                title="No commission rate existed when this campaign was submitted, so it earns no commission."
                                className="text-slate-400"
                              >
                                —
                              </span>
                            )}
                          </td>
                        )}
                        {c.net_revenue_cents > 0 || c.commission_cents > 0 ? (
                          <>
                            <td className={`${commissionsEnabled ? 'px-3' : 'px-5'} py-2.5 text-right tabular-nums text-slate-700`}>
                              {formatDollars(c.net_revenue_cents)}
                            </td>
                            {commissionsEnabled && (
                              <td className="px-5 py-2.5 text-right tabular-nums text-emerald-700">
                                {formatDollars(c.commission_cents)}
                              </td>
                            )}
                          </>
                        ) : (
                          // Attributed but nothing collected yet — say so
                          // instead of rendering a broken-looking $0.00.
                          <td
                            colSpan={commissionsEnabled ? 2 : 1}
                            className="px-3 py-2.5 text-right text-[12px] text-slate-400"
                            title="This campaign is credited to you, but no payment has been collected yet. Revenue and commission appear as charges land."
                          >
                            Awaiting Payment
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default PublisherMyReferralsPage;
